import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Play, Pause, SkipBack, SkipForward, Volume2, ArrowRight } from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";
const LOGO = "/brand/poodle-people.png?v=3";

function fmtTime(secs) {
  if (!Number.isFinite(secs)) return "0:00";
  const s = Math.floor(secs % 60);
  const m = Math.floor(secs / 60) % 60;
  const h = Math.floor(secs / 3600);
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}` : `${m}:${String(s).padStart(2, "0")}`;
}

function fmtDate(d) {
  if (!d) return "";
  try { return new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return ""; }
}

export default function PodcastSection({ limit = 6, showMoreLink = true } = {}) {
  const [data, setData] = useState({ show: null, episodes: [] });
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // episode object
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);

  const audioRef = useRef(null);

  useEffect(() => {
    // Use /episodes-full so we get slugs + admin-enriched fields alongside
    // the RSS data. Falls back to the plain /episodes payload if the new
    // endpoint isn't available (older deployments).
    fetch(`${API}/podcast/episodes-full`)
      .then(async (r) => r.ok ? r.json() : fetch(`${API}/podcast/episodes`).then((r2) => r2.json()))
      .then((d) => {
        setData(d);
        if (d.episodes && d.episodes.length) setActive(d.episodes[0]);
      })
      .catch((err) => { console.warn("podcast fetch failed:", err); })
      .finally(() => setLoading(false));
  }, []);

  // Reset audio state when active episode changes
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    setProgress(0);
    setDuration(0);
    setIsPlaying(false);
    el.load();
  }, [active?.guid]);

  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      el.play().catch((err) => console.warn("audio play failed:", err));
    } else {
      el.pause();
    }
  };

  const seek = (e) => {
    const el = audioRef.current;
    if (!el || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    el.currentTime = ratio * duration;
  };

  const skip = (delta) => {
    const el = audioRef.current;
    if (!el) return;
    el.currentTime = Math.max(0, Math.min(duration || 0, el.currentTime + delta));
  };

  const onVolumeChange = (e) => {
    const v = Number(e.target.value);
    setVolume(v);
    if (audioRef.current) audioRef.current.volume = v;
  };

  const pct = duration > 0 ? (progress / duration) * 100 : 0;

  return (
    <section id="podcast" className="py-16 sm:py-20 bg-foreground text-background" data-testid="podcast-section">
      <div className="max-w-6xl mx-auto px-6 sm:px-10">
        <div className="text-center mb-10">
          <p className="text-xs uppercase tracking-[0.22em] text-background/60">Listen</p>
          <h2 className="font-display text-3xl sm:text-5xl font-extrabold tracking-tighter mt-2">
            Poodle People Podcast
          </h2>
          <p className="text-sm text-background/80 mt-3 max-w-2xl mx-auto">
            {data.show?.description || "Each week Karin and Elise chat about their dog experiences, guests, and the standard poodle life."}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Player + recent episodes */}
          <div className="lg:col-span-2 space-y-5">
            {/* Player */}
            <Card className="border-2 border-background/15 bg-card text-foreground shadow-xl rounded-xl overflow-hidden" data-testid="podcast-player">
              <CardContent className="p-5 sm:p-6">
                <div className="flex items-start gap-5">
                  <img
                    src={active?.image || data.show?.image || LOGO}
                    alt={data.show?.title || "Poodle People Podcast"}
                    className="h-24 w-24 sm:h-28 sm:w-28 rounded-lg object-cover bg-foreground shrink-0"
                    onError={(e) => { e.currentTarget.src = LOGO; }}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{loading ? "Loading…" : (active?.duration || "")}</p>
                    <p className="font-display font-extrabold text-lg leading-tight mt-1 line-clamp-2" data-testid="podcast-now-playing">
                      {active?.title || "Pick an episode below"}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">{fmtDate(active?.pub_date)}</p>
                  </div>
                </div>

                {/* Progress bar */}
                <div
                  role="slider"
                  aria-valuemin={0}
                  aria-valuemax={duration || 0}
                  aria-valuenow={progress}
                  tabIndex={0}
                  className="mt-5 h-2 w-full rounded-full bg-muted relative cursor-pointer"
                  onClick={seek}
                  onKeyDown={(e) => { if (e.key === "ArrowRight") skip(15); if (e.key === "ArrowLeft") skip(-15); }}
                  data-testid="podcast-progress"
                >
                  <div className="h-full rounded-full bg-primary transition-[width] duration-150" style={{ width: `${pct}%` }} />
                </div>
                <div className="flex justify-between text-[11px] tabular-nums text-muted-foreground mt-1.5">
                  <span data-testid="podcast-progress-time">{fmtTime(progress)}</span>
                  <span>{fmtTime(duration)}</span>
                </div>

                {/* Controls */}
                <div className="mt-4 flex items-center gap-2 sm:gap-3">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => skip(-15)}
                    aria-label="Back 15 seconds"
                    data-testid="podcast-skip-back"
                  >
                    <SkipBack size={16} />
                  </Button>
                  <Button
                    size="icon"
                    onClick={toggle}
                    disabled={!active}
                    aria-label={isPlaying ? "Pause" : "Play"}
                    className="h-11 w-11 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                    data-testid="podcast-play-toggle"
                  >
                    {isPlaying ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => skip(30)}
                    aria-label="Forward 30 seconds"
                    data-testid="podcast-skip-forward"
                  >
                    <SkipForward size={16} />
                  </Button>
                  <div className="flex-1" />
                  <Volume2 size={16} className="text-muted-foreground hidden sm:block" />
                  <input
                    aria-label="Volume"
                    type="range"
                    min={0}
                    max={1}
                    step={0.05}
                    value={volume}
                    onChange={onVolumeChange}
                    className="w-20 hidden sm:block accent-primary"
                    data-testid="podcast-volume"
                  />
                </div>

                {active?.audio_url ? (
                  <audio
                    ref={audioRef}
                    src={active.audio_url}
                    preload="metadata"
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    onTimeUpdate={(e) => setProgress(e.currentTarget.currentTime)}
                    onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || 0)}
                    onEnded={() => setIsPlaying(false)}
                  />
                ) : null}
              </CardContent>
            </Card>

            {/* Episode list */}
            <Card className="border border-background/10 bg-card text-foreground rounded-xl overflow-hidden">
              <CardContent className="p-0">
                <ul className="divide-y divide-border" data-testid="podcast-episodes-list">
                  {loading ? (
                    <li className="p-6 text-sm text-muted-foreground text-center">Loading episodes…</li>
                  ) : (limit == null ? data.episodes : data.episodes.slice(0, limit)).map((ep) => (
                    <li key={ep.guid}>
                      <button
                        type="button"
                        onClick={() => { setActive(ep); }}
                        data-testid={`podcast-episode-${ep.guid}`}
                        className={`w-full text-left flex items-start gap-3 p-4 hover:bg-muted transition-colors ${
                          active?.guid === ep.guid ? "bg-muted" : ""
                        }`}
                      >
                        <span className={`h-9 w-9 rounded-md flex items-center justify-center shrink-0 ${
                          active?.guid === ep.guid && isPlaying
                            ? "bg-primary text-primary-foreground"
                            : "bg-secondary text-secondary-foreground"
                        }`}>
                          {active?.guid === ep.guid && isPlaying ? <Pause size={14} /> : <Play size={14} className="ml-0.5" />}
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="block font-medium leading-snug line-clamp-2">{ep.title}</span>
                          <span className="block text-[11px] uppercase tracking-[0.18em] text-muted-foreground mt-1">
                            {fmtDate(ep.pub_date)}{ep.duration ? ` · ${ep.duration}` : ""}
                          </span>
                        </span>
                      </button>
                      {ep.slug ? (
                        <Link
                          to={`/podcast/${ep.slug}`}
                          className="flex items-center gap-1 px-4 pb-3 text-xs text-primary hover:underline"
                          data-testid={`podcast-episode-notes-${ep.slug}`}
                        >
                          Show notes <ArrowRight size={11} />
                        </Link>
                      ) : null}
                    </li>
                  ))}
                </ul>
                {/* "More episodes" footer — only shown when the list is
                    truncated (home page use), linking to /podcast which
                    renders the full archive. */}
                {!loading && showMoreLink && limit != null && data.episodes.length > limit ? (
                  <Link
                    to="/podcast"
                    className="flex items-center justify-center gap-1.5 p-4 border-t border-border text-sm font-semibold text-primary hover:bg-muted transition-colors"
                    data-testid="podcast-see-all"
                  >
                    See all {data.episodes.length} episodes <ArrowRight size={13} />
                  </Link>
                ) : null}
                {/* Count banner when we're actually rendering everything. */}
                {!loading && limit == null && data.episodes.length > 0 ? (
                  <div className="p-3 border-t border-border text-center text-[11px] uppercase tracking-[0.2em] text-muted-foreground" data-testid="podcast-full-archive-count">
                    Showing all {data.episodes.length} episodes
                  </div>
                ) : null}
              </CardContent>
            </Card>
          </div>

          {/* Logo + subscribe */}
          <div className="space-y-5">
            <Card className="border border-background/10 bg-foreground text-background rounded-xl overflow-hidden">
              <CardContent className="p-6 text-center">
                <img
                  src={LOGO}
                  alt="Poodle People Podcast logo"
                  className="w-48 h-48 mx-auto object-contain"
                  data-testid="podcast-logo"
                />
                <p className="font-display text-lg font-extrabold tracking-tight mt-4">
                  {data.show?.title || "Poodle People"}
                </p>
                <p className="text-xs text-background/60 mt-1">{data.show?.author || "With hosts Elise and Karin"}</p>

                <p className="text-sm text-background/85 mt-5 leading-relaxed">
                  Subscribe on Spotify and Apple Podcasts so you don&apos;t miss an episode.
                </p>

                <div className="flex flex-col gap-2 mt-3" data-testid="podcast-platform-links">
                  <a
                    href="https://open.spotify.com/search/Poodle%20People%20Podcast"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="podcast-spotify-link"
                    className="inline-flex items-center justify-center gap-2 px-4 h-10 rounded-md bg-[#1DB954] text-black font-medium text-sm hover:opacity-90 transition-opacity"
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
                      <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.84-.179-.94-.539-.12-.421.18-.84.54-.94 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.342 1.06zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.42 1.56-.301.421-1.02.599-1.561.3z"/>
                    </svg>
                    Listen on Spotify
                  </a>
                  <a
                    href="https://podcasts.apple.com/search?term=Poodle%20People%20Podcast"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="podcast-apple-link"
                    className="inline-flex items-center justify-center gap-2 px-4 h-10 rounded-md bg-background text-foreground font-medium text-sm hover:opacity-90 transition-opacity"
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
                      <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 4.5a3.75 3.75 0 11-.001 7.501A3.75 3.75 0 0112 4.5zm.06 8.51c2.42 0 4.6 1.31 5.71 3.39.43.81-.32 1.74-1.22 1.49-1.69-.47-3.04-.7-4.49-.7-1.45 0-2.8.23-4.49.7-.9.25-1.65-.68-1.22-1.49 1.11-2.08 3.29-3.39 5.71-3.39z"/>
                    </svg>
                    Listen on Apple Podcasts
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
}
